/**
 * DOM helpers for the guide's surfaces.
 *
 * UI-agnostic logic: plain DOM, no React, no component library. The code is independent of
 * your UI library; the two constants that may need a look are `FLASH_COLOR` (the outline drawn
 * around a located control — match your primary colour) and nothing else. The `data-help` /
 * `data-help-panel` attribute names are the contract and must stay.
 */
import type { HelpStep } from './types';
import { anchorNameOf } from './types';

/**
 * DOM helpers shared by the drawer ("locate this control"), the tour (spotlight) and the
 * inspector (click a field, read what it does). Split from the components so all three agree
 * on what "on screen" means — a guide that highlights a hidden element is worse than one that
 * says "not here right now".
 */

/**
 * Marks the guide's own surfaces (the drawer, the tour card, the inspector's overlay) so they
 * are never mistaken for the dialog the user is working in — nor, in the inspector, for a
 * control the user was pointing at.
 */
export const HELP_PANEL_ATTR = 'data-help-panel';

/** The attribute every guide step targets. Built as a string so the checker sees no phantom. */
const ANCHOR_ATTR = 'data-help';

/**
 * Is this node actually showing? Shared so "on screen" means one thing everywhere — the single
 * lookup below and the multi-candidate scan `flashAnchor` does for a surface opener.
 */
const isVisible = (el: HTMLElement): boolean => {
  // getClientRects() is empty for `display: none`, an unmounted-but-cached node, and an
  // element inside a collapsed container — cheaper and more honest than reading styles.
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
};

export const findStepElement = (
  selector: string,
  /** Restrict the lookup to one subtree — the open dialog, when there is one. */
  scope: ParentNode = document,
): HTMLElement | null => {
  const el = scope.querySelector<HTMLElement>(selector);
  if (!el) return null;
  return isVisible(el) ? el : null;
};

export const isStepOnScreen = (step: HelpStep, scope: ParentNode = document): boolean =>
  findStepElement(step.selector, scope) !== null;

/**
 * Anchor name → step, built once per guide so the inspector can resolve a pointer position
 * without re-scanning the step list on every mouse move.
 */
export const anchorIndex = (steps: HelpStep[]): Map<string, HelpStep> => {
  const index = new Map<string, HelpStep>();
  for (const step of steps) {
    const anchor = anchorNameOf(step.selector);
    // First wins. Selectors are unique within a guide (`registry.test.ts` proves it), so a
    // collision would be a data bug rather than a precedence question worth expressing here.
    if (anchor && !index.has(anchor)) index.set(anchor, step);
  }
  return index;
};

export interface AnchoredStep {
  step: HelpStep;
  /** The element the step is actually about — the one to outline, not the one clicked. */
  element: HTMLElement;
}

/**
 * The guide entry for whatever the user is pointing at, walking outwards from the exact node
 * under the cursor.
 *
 * A click lands on an `<input>`, a label or an SVG path — never on the wrapper carrying
 * `data-help` — so the anchor has to be found by walking up. Two rules make that walk honest:
 *
 *  - **Innermost documented ancestor wins.** An anchored field inside an anchored section is
 *    the more specific answer, and the specific answer is the one that was asked for.
 *  - **An anchor with no step is walked THROUGH, not stopped at.** Plenty of `data-help`
 *    attributes exist that no guide documents yet; stopping at the first one would answer
 *    "nothing here" while the section around it is documented perfectly well.
 *
 * The guide's own panels resolve to nothing: pointing at the inspector's own card is not a
 * question about the page.
 */
export const stepAtElement = (
  target: EventTarget | null,
  index: Map<string, HelpStep>,
): AnchoredStep | null => {
  if (!(target instanceof Element)) return null;
  if (target.closest(`[${HELP_PANEL_ATTR}]`)) return null;

  let el = target.closest(`[${ANCHOR_ATTR}]`);
  while (el) {
    const step = index.get(el.getAttribute(ANCHOR_ATTR) ?? '');
    if (step && el instanceof HTMLElement) return { step, element: el };
    el = el.parentElement?.closest(`[${ANCHOR_ATTR}]`) ?? null;
  }
  return null;
};

/**
 * The modal dialog the user is currently working in, if any.
 *
 * A tour started while a dialog is open must stay INSIDE it: the page behind the backdrop
 * is not reachable, so spotlighting a filter tab under a modal points at something the
 * user cannot click. The topmost open dialog wins (a dialog can open another), and the
 * guide's own drawer and tour card are excluded — they are not the user's work surface.
 */
export const activeDialogSurface = (): HTMLElement | null => {
  const open = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]')).filter(
    (el) => !el.hasAttribute(HELP_PANEL_ATTR) && el.getClientRects().length > 0,
  );
  // Later in document order = rendered on top (portals append).
  return open.length > 0 ? (open[open.length - 1] ?? null) : null;
};

const FLASH_COLOR = '#235592'; // EDIT: your primary colour
const FLASH_MS = 1800;
/** The guide slides back once the outline has faded, plus a beat for the slide itself. */
const YIELD_MS = FLASH_MS + 250;

/**
 * Is the guide's own panel sitting on top of this control?
 *
 * The drawer takes 440px of the right edge, which is exactly where every list page keeps its
 * primary action — so "show me where this is" was outlining controls nobody could see. Vertical
 * `scrollIntoView` cannot fix that; only the panel moving can, and it should only move when it
 * is genuinely in the way.
 */
export const isOccludedByPanel = (el: HTMLElement): boolean => {
  const target = el.getBoundingClientRect();
  if (target.width === 0 && target.height === 0) return false;
  return Array.from(document.querySelectorAll<HTMLElement>(`[${HELP_PANEL_ATTR}]`)).some((panel) => {
    const p = panel.getBoundingClientRect();
    if (p.width === 0 && p.height === 0) return false;
    return (
      target.left < p.right && target.right > p.left && target.top < p.bottom && target.bottom > p.top
    );
  });
};

/**
 * The panel is asked to step aside and to come back — never told how. `dom.ts` owns WHEN
 * (it owns the flash duration); the drawer owns HOW (a React state driving a transform), so
 * nothing here reaches into the component library's internals.
 */
export type YieldHandler = (yielding: boolean) => void;

let yieldTimer = 0;
let yieldingTo: YieldHandler | null = null;

export interface LocateOptions {
  /** Called with `true` when the guide is covering the target, and `false` once it may return. */
  onYield?: YieldHandler;
}

/**
 * Scroll a control into view and flash an outline around it. Returns false when the
 * element is not currently rendered, so the caller can explain why instead of doing
 * nothing (the usual cause: the step lives behind a dialog that is not open).
 */
export const locateElement = (step: HelpStep, options: LocateOptions = {}): boolean => {
  const el = findStepElement(step.selector);
  if (!el) return false;

  if (options.onYield) {
    // While already yielded the panel is off-screen, so its rect no longer says anything about
    // what it covers: a locate during a yield extends it instead of re-measuring. That also
    // makes locating several controls in a row feel deliberate rather than flickery.
    if (yieldingTo !== null || isOccludedByPanel(el)) {
      window.clearTimeout(yieldTimer);
      yieldingTo = options.onYield;
      options.onYield(true);
      yieldTimer = window.setTimeout(() => {
        yieldingTo?.(false);
        yieldingTo = null;
      }, YIELD_MS);
    }
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const previousOutline = el.style.outline;
  const previousOffset = el.style.outlineOffset;
  const previousRadius = el.style.borderRadius;
  el.style.outline = `3px solid ${FLASH_COLOR}`;
  el.style.outlineOffset = '3px';
  if (!previousRadius) el.style.borderRadius = '4px';
  window.setTimeout(() => {
    el.style.outline = previousOutline;
    el.style.outlineOffset = previousOffset;
    el.style.borderRadius = previousRadius;
  }, FLASH_MS);

  return true;
};

/** Component libraries mark a disabled control either way, depending on the element it renders. */
const isPressable = (el: HTMLElement): boolean =>
  !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true';

/** How long a pressed control gets to put a dialog on screen before we point at it instead. */
const OPENED_CHECK_MS = 700;
/** How much of a fresh budget the poll gets once a surface has been opened to render into. */
const SURFACE_GRACE_MS = 2500;

/**
 * Why the poll ended without the anchor being outlined.
 *
 * `behind-surface` is not quite a failure: the control that LEADS to the anchor was flashed
 * instead, because there was more than one of it on screen and choosing between them is the
 * user's call. `not-on-screen` is the honest one — nothing was pointed at, and the caller owes
 * the user a sentence saying so.
 */
export type MissReason = 'not-on-screen' | 'behind-surface';

export interface RevealAnchorOptions {
  /**
   * Press the control once it is found instead of only outlining it. Only ever true for an
   * Ask entry that opted in (`anchor_action: open`), and only for controls that open a
   * form and write nothing — see references/ask-catalog.md.
   */
  activate?: boolean;
  timeoutMs?: number;
  /**
   * Same contract as `locateElement`: "Show me the control" is pressed from the drawer, so the
   * drawer can be the very thing hiding what it just outlined.
   */
  onYield?: YieldHandler;
  /**
   * `anchor_surface` from the Ask entry: the control that opens the dialog, drawer or wizard
   * the anchor lives inside. Being on the right ROUTE is not being on the right SCREEN, and a
   * form is not a route — see the field's own note in references/ask-catalog.md.
   */
  surface?: string | null;
  /**
   * Called once when the poll ends with the anchor never outlined. The one thing this whole
   * function must not do is finish quietly: a caller that cannot report the miss leaves the user
   * looking at a button that did nothing, which is the bug this callback exists to close.
   */
  onMissing?: (reason: MissReason) => void;
  /**
   * Called just before a surface is opened. The guide's own drawer sits above the modal layer,
   * so a dialog opened underneath it would be invisible — the panel is told a form is coming and
   * decides for itself whether to stand down.
   */
  onHandover?: () => void;
}

/**
 * Flash a `data-help` anchor by name — or press it — retrying until the screen that owns it has
 * rendered.
 *
 * The assistant navigates first and points second: after `navigate('/orders')` the route's
 * data is still loading, so a single lookup would find nothing and the user would arrive on the
 * right page with no idea which control was meant. Polling for a few seconds is the difference
 * between "here it is" and "somewhere on this page".
 *
 * With `activate`, the same poll ends in a click. Users read a highlighted "New order"
 * button as the assistant stopping one step short of the answer, so an entry whose job *starts*
 * in a dialog opens it. Two guards keep that from being a click into the dark:
 *   - a disabled control is flashed, never pressed — greyed out is itself the answer to "why
 *     can't I do this?";
 *   - a press that opens no dialog falls back to flashing, so a wrong opt-in in the Ask entry
 *     degrades to the old behaviour instead of to nothing visible happening.
 *
 * With `surface`, the poll may take one step of its own on the way. Being on the anchor's ROUTE
 * is not being on its SCREEN: half the controls users ask about live inside a dialog, and for
 * those the old poll queried a list page for four seconds and expired without a trace — the same
 * silent no-op `anchorRoute` was introduced to kill, one layer further in. Now the surface is
 * opened first and the anchor flashed inside it.
 *
 * Whatever happens, the poll reports itself: `onMissing` fires when nothing was outlined, so
 * "the button does nothing" is no longer a state this function can leave the user in.
 *
 * Returns a cancel function — a component that unmounts (drawer closed) must stop polling.
 */
export const flashAnchor = (anchor: string, options: RevealAnchorOptions = {}): (() => void) => {
  const {
    activate = false,
    timeoutMs = 4000,
    onYield,
    surface = null,
    onMissing,
    onHandover,
  } = options;
  const step: HelpStep = { selector: `[data-help="${anchor}"]`, title: anchor, body: '' };
  let deadline = Date.now() + timeoutMs;
  let timer = 0;
  let surfaceHandled = false;

  const press = (el: HTMLElement): void => {
    const before = activeDialogSurface();
    el.click();
    timer = window.setTimeout(() => {
      if (activeDialogSurface() === before) locateElement(step, { onYield });
    }, OPENED_CHECK_MS);
  };

  /**
   * The anchor is not on screen and the entry names the control that leads to it.
   *
   * Pressed only when there is exactly ONE of that control: a row action exists once per row,
   * and which record the user meant is the one thing the panel may never decide for them. An
   * ambiguous opener is flashed instead — that answers "where do I do this" without answering
   * "to which record" on their behalf.
   *
   * Returns whether the poll should continue.
   */
  const revealSurface = (name: string): boolean => {
    const visible = Array.from(
      document.querySelectorAll<HTMLElement>(`[data-help="${name}"]`),
    ).filter(isVisible);
    // Nothing yet — the route's data is still loading. Say nothing and try again next tick.
    if (visible.length === 0) return true;

    surfaceHandled = true;
    const only = visible.length === 1 ? visible[0] : undefined;
    if (only && isPressable(only)) {
      onHandover?.();
      only.click();
      // A dialog mounts, then fetches, so it gets a floor of its own — `max` rather than a
      // reset, because a generous `timeoutMs` must not come out SHORTER for having been quick
      // to find the opener.
      deadline = Math.max(deadline, Date.now() + SURFACE_GRACE_MS);
      return true;
    }
    locateElement({ selector: `[data-help="${name}"]`, title: name, body: '' }, { onYield });
    onMissing?.('behind-surface');
    return false;
  };

  const attempt = (): void => {
    const el = findStepElement(step.selector);
    if (el) {
      if (activate && isPressable(el)) press(el);
      else locateElement(step, { onYield });
      return;
    }
    if (surface && !surfaceHandled && !revealSurface(surface)) return;
    if (Date.now() > deadline) {
      onMissing?.('not-on-screen');
      return;
    }
    timer = window.setTimeout(attempt, 160);
  };
  attempt();

  return () => window.clearTimeout(timer);
};
